import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSearch, type SearchResults } from '../../api/admin.js';
import { shortId } from '../../lib/format.js';
import { Icon, type IconName } from './icons.js';

interface CmdKProps {
  open: boolean;
  onClose: () => void;
}

interface Hit {
  key: string;
  href: string;
  icon: IconName;
  title: string;
  titleMono: boolean;
  meta: string;
  groupLabel: 'Users' | 'Sessions' | 'Traces';
}

const EMPTY: SearchResults = { users: [], sessions: [], traces: [] };

export function CmdK({ open, onClose }: CmdKProps): ReactElement {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const enabled = open && query.trim().length >= 2;
  const search = useQuery({
    queryKey: ['cmdk-search', query.trim()],
    queryFn: () => fetchSearch(query.trim()),
    enabled,
    staleTime: 5_000,
  });

  const results: SearchResults = useMemo(() => search.data ?? EMPTY, [search.data]);

  const hits = useMemo<Hit[]>(() => {
    const out: Hit[] = [];
    for (const u of results.users) {
      out.push({
        key: `u-${u.id}`,
        href: `/users/${u.id}`,
        icon: 'user',
        title: u.email,
        titleMono: false,
        meta: u.role,
        groupLabel: 'Users',
      });
    }
    for (const s of results.sessions) {
      out.push({
        key: `s-${s.id}`,
        href: `/sessions/${s.id}`,
        icon: 'session',
        title: shortId(s.id),
        titleMono: true,
        meta: `${s.userEmail} · ${s.moduleId ?? '—'}`,
        groupLabel: 'Sessions',
      });
    }
    for (const t of results.traces) {
      out.push({
        key: `t-${t.traceId}`,
        href: t.firstSessionId ? `/sessions/${t.firstSessionId}` : '',
        icon: 'trace',
        title: shortId(t.traceId),
        titleMono: true,
        meta: `${t.spanCount} spans`,
        groupLabel: 'Traces',
      });
    }
    return out;
  }, [results]);

  useEffect(() => {
    // reset cursor when results change
    setCursor(0);
  }, [hits]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (hits.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, hits.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const hit = hits[cursor];
        if (hit?.href) {
          onClose();
          void navigate(hit.href);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hits, cursor, onClose, navigate]);

  function go(hit: Hit): void {
    if (!hit.href) return;
    onClose();
    void navigate(hit.href);
  }

  // Group hits by groupLabel preserving order.
  const groups = useMemo(() => {
    const map = new Map<Hit['groupLabel'], Hit[]>();
    for (const h of hits) {
      const arr = map.get(h.groupLabel);
      if (arr) arr.push(h);
      else map.set(h.groupLabel, [h]);
    }
    return Array.from(map.entries());
  }, [hits]);

  return (
    <div
      className={`cmdk-overlay${open ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="cmdk"
    >
      <div className="cmdk">
        <div className="cmdk-input-wrap">
          <Icon name="search" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by email, session ID, trace ID, provider request ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="cmdk-input"
          />
          <span className="kbd">esc</span>
        </div>
        <div className="cmdk-results">
          {!enabled ? (
            <div className="cmdk-empty">Escribí al menos 2 caracteres</div>
          ) : search.isPending ? (
            <div className="cmdk-empty">Buscando…</div>
          ) : search.isError ? (
            <div className="cmdk-empty">Error: {search.error.message}</div>
          ) : hits.length === 0 ? (
            <div className="cmdk-empty">Sin resultados</div>
          ) : (
            groups.map(([groupLabel, groupHits]) => (
              <div key={groupLabel} className="cmdk-group">
                <div className="cmdk-group-label">
                  {groupLabel} · {groupHits.length}
                </div>
                {groupHits.map((hit) => {
                  const idx = hits.indexOf(hit);
                  const isActive = idx === cursor;
                  return (
                    <div
                      key={hit.key}
                      className={`cmdk-item${isActive ? ' is-active' : ''}`}
                      onClick={() => go(hit)}
                      onMouseEnter={() => setCursor(idx)}
                      data-testid={`cmdk-hit-${hit.key}`}
                    >
                      <span className="cmdk-item-icon">
                        <Icon name={hit.icon} />
                      </span>
                      <div className="cmdk-item-main">
                        <span className={`cmdk-item-title${hit.titleMono ? ' mono' : ''}`}>
                          {hit.title}
                        </span>
                        <span className="cmdk-item-meta">{hit.meta}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="cmdk-footer">
          <span className="hint">
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> Navigate
          </span>
          <span className="hint">
            <span className="kbd">↵</span> Open
          </span>
          <span className="hint">
            <span className="kbd">esc</span> Close
          </span>
          {hits.length > 0 ? (
            <span className="hint" style={{ marginLeft: 'auto' }}>
              {groups.length} sources · {hits.length} matches
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
